// LINE menu designer, shared by the owner page (จัดการร้าน → รูปเมนู LINE) and the Console.
// Brand bar = logo picture and/or name, banner = the salon's photo, 6 buttons in a colour theme. Empty slots stay blank.
import { useEffect, useRef, useState } from 'react';
import { BRANDED_HEIGHT, MENU_WIDTH, layoutForSize } from '@core/richmenu.ts';
import type { MenuExtras } from '../api/types.ts';
import { message } from '../lib/errors.ts';
import { Banner, Button, Card, Field, useToast } from '../ui/index.tsx';
import { drawCustomerMenu, drawStaffMenu, THEMES, type MenuTheme } from './menuArt.ts';
import { trimLogo } from './trim.ts';

const MAX_BYTES = 1_000_000;
type Picture = HTMLCanvasElement | HTMLImageElement;

function readAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** Decodes a picked file and scales it down to at most `maxW`×`maxH` (keeps phone memory and storage small). */
async function loadPicture(file: File, maxW: number, maxH: number): Promise<HTMLCanvasElement | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    const bmp = await createImageBitmap(file);
    const s = Math.min(1, maxW / bmp.width, maxH / bmp.height);
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(bmp.width * s));
    c.height = Math.max(1, Math.round(bmp.height * s));
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close();
    return c;
  } catch {
    return null;
  }
}

/** Small copy of the logo for the booking screens' header (PNG keeps a see-through background). */
function smallLogo(logo: Picture): string | null {
  const s = Math.min(1, 512 / logo.width, 256 / logo.height);
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(logo.width * s));
  c.height = Math.max(1, Math.round(logo.height * s));
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(logo, 0, 0, c.width, c.height);
  const png = c.toDataURL('image/png');
  const url = png.length < 380_000 ? png : c.toDataURL('image/jpeg', 0.9);
  return url.split(',')[1] ?? null;
}

/** The staff menu (คิววันนี้ · ปฏิทิน · จองให้ลูกค้า · จัดการร้าน) drawn in the same theme, as base64 PNG. */
async function staffMenuPicture(theme: MenuTheme): Promise<string | undefined> {
  const c = document.createElement('canvas');
  c.width = MENU_WIDTH;
  c.height = 843;
  const ctx = c.getContext('2d');
  if (!ctx) return undefined;
  drawStaffMenu(ctx, theme);
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/png'));
  return blob ? readAsBase64(blob) : undefined;
}

/** JPEG under LINE's 1 MB limit: lowers quality step by step. */
async function toJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  for (const q of [0.92, 0.86, 0.8, 0.72, 0.64, 0.55]) {
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', q));
    if (blob && blob.size <= MAX_BYTES) return blob;
  }
  return null;
}

// Remembered per shop in this browser, so the owner can come back and change one thing. Failures are ignored.
interface Saved { theme: string; name: string; tagline: string; focus: number }
function readStore<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v) as T;
  } catch {
    return fallback;
  }
}
function writeStore(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch { /* full or private mode */ }
}
function restorePicture(key: string, set: (p: Picture) => void, trim = false) {
  const url = readStore<string | null>(key, null);
  if (!url || typeof Image === 'undefined') return;
  const img = new Image();
  img.onload = () => set(trim ? trimLogo(img) : img);
  img.src = url;
}

/** A copy no bigger than maxW × maxH. */
function fit(pic: Picture, maxW: number, maxH: number): Picture {
  const s = Math.min(1, maxW / pic.width, maxH / pic.height);
  if (s === 1) return pic;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(pic.width * s));
  c.height = Math.max(1, Math.round(pic.height * s));
  c.getContext('2d')?.drawImage(pic, 0, 0, c.width, c.height);
  return c;
}

export function MenuDesigner({ storeKey, shopName, upload }: {
  /** Browser storage key for this shop's choices. */
  storeKey: string;
  shopName: string;
  /** Extras are sent with a designed menu (not with a ready-made picture): the booking screens and staff menu match it. */
  upload: (imageBase64: string, contentType: string, extras?: MenuExtras) => Promise<unknown>;
}) {
  const toast = useToast();
  const saved = useRef(readStore<Partial<Saved>>(storeKey, {})).current;
  const [themeId, setThemeId] = useState(saved.theme && THEMES.some((t) => t.id === saved.theme) ? saved.theme : THEMES[0].id);
  const [name, setName] = useState(saved.name ?? '');
  const [tagline, setTagline] = useState(saved.tagline ?? '');
  const [focus, setFocus] = useState(saved.focus ?? 50);
  const [logo, setLogo] = useState<Picture | null>(null);
  const [photo, setPhoto] = useState<Picture | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'design' | 'own' | null>(null);
  const [ownFile, setOwnFile] = useState<File | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = THEMES.find((t) => t.id === themeId) ?? THEMES[0];

  useEffect(() => {
    restorePicture(`${storeKey}:logo`, setLogo, true);
    restorePicture(`${storeKey}:photo`, setPhoto);
  }, [storeKey]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) drawCustomerMenu(ctx, { theme, name, tagline, logo, photo, photoFocus: focus });
    writeStore(storeKey, { theme: themeId, name, tagline, focus } satisfies Saved);
  }, [theme, themeId, name, tagline, logo, photo, focus, storeKey]);

  async function pick(f: File | null, kind: 'logo' | 'photo') {
    setError(null);
    if (!f) return;
    if (f.type && !f.type.startsWith('image/')) { setError('กรุณาเลือกไฟล์รูปภาพ'); return; }
    const pic = kind === 'logo' ? await loadPicture(f, 3000, 3000) : await loadPicture(f, MENU_WIDTH, 2000);
    if (!pic) { setError('เปิดรูปนี้ไม่ได้ ลองใช้ไฟล์ JPG หรือ PNG'); return; }
    if (kind === 'logo') {
      // Cut the empty border first (at full size), then keep it small.
      const mark = fit(trimLogo(pic), 1200, 600);
      setLogo(mark);
      writeStore(`${storeKey}:logo`, (mark as HTMLCanvasElement).toDataURL ? (mark as HTMLCanvasElement).toDataURL('image/png') : null);
    } else {
      setPhoto(pic);
      setFocus(50);
      writeStore(`${storeKey}:photo`, pic.toDataURL('image/jpeg', 0.85));
    }
  }

  function clear(kind: 'logo' | 'photo') {
    if (kind === 'logo') setLogo(null);
    else setPhoto(null);
    writeStore(`${storeKey}:${kind}`, null);
  }

  async function send(blob: Blob, type: string, kind: 'design' | 'own', extras?: MenuExtras) {
    setBusy(kind);
    setError(null);
    try {
      await upload(await readAsBase64(blob), type, extras);
      toast(kind === 'design' ? 'บันทึกแล้ว เมนู LINE และหน้าจองจะเปลี่ยนเมื่อลูกค้าเปิดใหม่' : 'เปลี่ยนเมนู LINE แล้ว ลูกค้าจะเห็นเมื่อเปิดแชทใหม่', 'success');
      if (kind === 'own') setOwnFile(null);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  async function saveDesign() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) { setError('เครื่องนี้สร้างรูปไม่ได้ ลองเปิดในมือถือหรือคอมพิวเตอร์เครื่องอื่น'); return; }
    if (document.fonts) await document.fonts.ready;
    drawCustomerMenu(ctx, { theme, name, tagline, logo, photo, photoFocus: focus });
    const blob = await toJpeg(canvas);
    if (!blob) { setError('รูปใหญ่เกิน 1 MB ลองใช้รูปแบนเนอร์ที่เล็กลง'); return; }
    await send(blob, 'image/jpeg', 'design', {
      brand: { theme: theme.id, tagline: tagline.trim() || null, logoBase64: logo ? smallLogo(logo) : null },
      staffImageBase64: await staffMenuPicture(theme),
    });
  }

  async function pickOwn(f: File | null) {
    setError(null);
    setOwnFile(null);
    if (!f) return;
    if (!['image/png', 'image/jpeg'].includes(f.type)) { setError('ใช้ไฟล์ PNG หรือ JPG เท่านั้น'); return; }
    if (f.size > MAX_BYTES) { setError('ไฟล์ต้องไม่เกิน 1 MB'); return; }
    if (typeof createImageBitmap === 'function') {
      const b = await createImageBitmap(f).catch(() => null);
      if (b && !layoutForSize(b.width, b.height)) {
        setError(`ขนาดรูปต้องเป็น 2500×1336 หรือ 2500×843 พิกเซล (รูปนี้ ${b.width}×${b.height})`);
        return;
      }
    }
    setOwnFile(f);
  }

  const fileButton = (label: string, kind: 'logo' | 'photo') => (
    <label className="btn btn-secondary btn-sm">
      {label}
      <input type="file" accept="image/*" hidden onChange={(e) => { void pick(e.target.files?.[0] ?? null, kind); e.target.value = ''; }} />
    </label>
  );

  return (
    <>
      <p className="small muted" style={{ marginBottom: 10 }}>
        เมนูด้านล่างแชท LINE: แถบชื่อร้าน · รูปแบนเนอร์ · ปุ่ม 4 ปุ่ม (ช่องที่ไม่ใส่จะเว้นว่างไว้)
        · โลโก้ คำโปรย และสีจะใช้กับหน้าจองคิวด้วย
      </p>
      <canvas ref={canvasRef} width={MENU_WIDTH} height={BRANDED_HEIGHT} className="menu-preview" role="img" aria-label="ตัวอย่างเมนู LINE" />

      <p className="section-title">1 · แถบชื่อร้าน (ด้านบน)</p>
      <Card>
        <p className="small" style={{ marginBottom: 8 }}>
          รูปโลโก้ร้าน: โลโก้แนวนอนที่มีชื่อร้านจะแสดงเต็มแถบ · โลโก้สี่เหลี่ยมจะแสดงคู่กับชื่อร้านด้านล่าง
        </p>
        <div className="row" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
          {fileButton(logo ? 'เปลี่ยนรูปโลโก้' : 'เลือกรูปโลโก้', 'logo')}
          {logo && <Button variant="secondary" className="btn-sm" onClick={() => clear('logo')}>เอาโลโก้ออก</Button>}
        </div>
        <Field label="ชื่อร้าน (ถ้ามี)" hint={`เว้นว่างได้ · เช่น ${shopName}`}>
          <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="คำโปรย (ถ้ามี)" hint="เช่น Nail & Beauty Studio หรือ ร้านทำเล็บใกล้ BTS อารีย์">
          <input value={tagline} maxLength={50} onChange={(e) => setTagline(e.target.value)} />
        </Field>
      </Card>

      <p className="section-title">2 · รูปแบนเนอร์</p>
      <Card>
        <p className="small" style={{ marginBottom: 8 }}>รูปหน้าร้าน ภายในร้าน หรือผลงานสวย ๆ (ระบบตัดให้พอดีเอง) · แตะแบนเนอร์ = จองคิว</p>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {fileButton(photo ? 'เปลี่ยนรูปแบนเนอร์' : 'เลือกรูปแบนเนอร์', 'photo')}
          {photo && <Button variant="secondary" className="btn-sm" onClick={() => clear('photo')}>เอารูปออก</Button>}
        </div>
        {photo && (
          <div style={{ marginTop: 12 }}>
            <Field label="เลื่อนรูป (บน ↔ ล่าง)">
              <input type="range" min={0} max={100} value={focus} onChange={(e) => setFocus(Number(e.target.value))} />
            </Field>
          </div>
        )}
      </Card>

      <details className="theme-more" open={themeId !== THEMES[0].id}>
        <summary>
          <span>สี: <b>{theme.label}</b>{themeId === THEMES[0].id ? ' (มาตรฐานทุกร้าน)' : ''}</span>
          <span className="muted small">เปลี่ยนสี (ไม่บังคับ)</span>
        </summary>
        <p className="small muted" style={{ margin: '4px 0 10px' }}>ทุกร้านใช้สีขาวทองได้เลย ไม่ต้องเลือก · เปลี่ยนเฉพาะร้านที่ขอสีอื่น</p>
        <div className="theme-grid" role="group" aria-label="สีธีม">
          {THEMES.map((t) => (
            <button key={t.id} type="button" className="theme-swatch" aria-pressed={t.id === themeId} onClick={() => setThemeId(t.id)}>
              <span className="theme-dot" style={{ background: t.bg, borderColor: t.line }}><span style={{ background: t.line }} /></span>
              {t.label}
            </button>
          ))}
        </div>
      </details>

      <div style={{ height: 16 }} />
      {error && <Banner kind="error">{error}</Banner>}
      <Button block loading={busy === 'design'} disabled={busy !== null} onClick={() => void saveDesign()}>บันทึกและใช้เป็นเมนู LINE</Button>

      <details className="own-menu">
        <summary>มีรูปเมนูที่ออกแบบเองแล้ว?</summary>
        <p className="small" style={{ margin: '8px 0' }}>
          2500×1336 พิกเซล = แบบ 4 ปุ่ม (แถบบน 0–220 · แบนเนอร์ 220–955 · ปุ่ม 1 แถว × 4: จองคิว · การจองของฉัน · โทรหาร้าน · บริการและราคา)<br />
          2500×843 พิกเซล = แบบไม่มีแบนเนอร์ 4 ปุ่ม (จองคิว · การจองของฉัน · ข้อมูลร้าน · โทรหาร้าน)<br />
          ไฟล์ PNG/JPG ไม่เกิน 1 MB
        </p>
        <label className="btn btn-secondary btn-block" style={{ marginBottom: 10 }}>
          เลือกรูป
          <input type="file" accept="image/png,image/jpeg" hidden onChange={(e) => { void pickOwn(e.target.files?.[0] ?? null); e.target.value = ''; }} />
        </label>
        {ownFile && <p className="small" style={{ marginBottom: 10 }}>{ownFile.name} พร้อมอัปโหลด</p>}
        <Button block variant="secondary" disabled={!ownFile || busy !== null} loading={busy === 'own'}
          onClick={() => ownFile && void send(ownFile, ownFile.type, 'own')}>อัปโหลดรูปของฉัน</Button>
      </details>
    </>
  );
}
